import React, { useMemo } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import NtTextInput from "../../components/NtTextInput";
import NtButton from "../../components/NtButton";
import { getAuthService } from "@ntconnect/framework";
import { showToast } from "../../common/utils/ControllerUtils";
import NtText from "../../components/NtText";
import { useTheme } from "../../context/ThemeContext";
import { useMobile } from "../../hooks/useMobile";
import routes from "../../navigation/routes";
import { getPreferences } from "../../config/preferences";
import BaseAuthController from "./BaseAuthController";
import { navigationRef } from "../../../App";
import { fetchPermissions } from "../../api/rbac";
import NtOnboardingCtaBlock from "../../onboarding/components/NtOnboardingCtaBlock";
import NtVoxcraftBrand from "../../marketing/components/voxcraft/NtVoxcraftBrand";
import settings from "../../config/settings";
import packageJson from "../../../package.json";

// Faded grid backdrop behind the login card. Uses a raw <div> so we can use
// CSS background-image + mask-image (web only). The page is web-only anyway.
function GridBackdrop() {
  const { theme } = useTheme();
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        backgroundImage: `linear-gradient(${theme.border} 1px, transparent 1px), linear-gradient(90deg, ${theme.border} 1px, transparent 1px)`,
        backgroundSize: "48px 48px",
        WebkitMaskImage:
          "radial-gradient(ellipse at center, #000 40%, transparent 90%)",
        maskImage:
          "radial-gradient(ellipse at center, #000 40%, transparent 90%)",
        opacity: 1,
      }}
    />
  );
}

// Left-side brand panel — pitch + module list. Hook component so it can read
// theme inside a class controller.
function BrandPanel() {
  const { theme } = useTheme();
  const styles = useMemo(() => makeBrandStyles(theme), [theme]);
  const modules = [
    { letter: "E", name: "Extensions", meta: "USERS · LINES" },
    { letter: "R", name: "Ring Groups", meta: "CALL DIST" },
    { letter: "I", name: "IVR Menus", meta: "AUTO ATTND" },
    { letter: "V", name: "Voicemail", meta: "BOXES · MSG" },
    { letter: "D", name: "Dial Plans", meta: "XML · ROUTING" },
  ];
  return (
    <View style={styles.panel}>
      <View style={styles.logo}>
        <NtVoxcraftBrand size={34} fontSize={22} />
      </View>

      <NtText style={styles.title}>Run your PBX from one console.</NtText>
      <NtText style={styles.lede}>
        Extensions, ring groups, IVR menus, voicemail, dial plans, inbound
        routes, and PIN-billed calls — all administered from one place.
      </NtText>

      <View style={styles.moduleLabel}>
        <NtText style={styles.moduleLabelText}>What you can manage</NtText>
      </View>
      {modules.map((m) => (
        <View key={m.letter} style={styles.moduleRow}>
          <View style={styles.moduleIcon}>
            <NtText style={styles.moduleIconText}>{m.letter}</NtText>
          </View>
          <NtText style={styles.moduleName}>{m.name}</NtText>
          <NtText style={styles.moduleMeta}>{m.meta}</NtText>
        </View>
      ))}

      <NtOnboardingCtaBlock />

      <NtText style={styles.footerMeta}>Voxcraft · v{packageJson.version}</NtText>
    </View>
  );
}

const makeBrandStyles = (t) =>
  StyleSheet.create({
    panel: {
      flex: 1.05,
      backgroundColor: t.surface,
      borderRightWidth: 1,
      borderRightColor: t.borderSoft,
      padding: 56,
    },
    // Wrapper around NtVoxcraftBrand; matches the spacing the old PNG logo
    // had so the rest of the panel layout stays put.
    logo: { marginBottom: 56 },
    title: {
      fontSize: 38,
      lineHeight: 42,
      fontWeight: "700",
      letterSpacing: -0.6,
      color: t.text,
      maxWidth: 460,
      marginBottom: 12,
    },
    lede: {
      color: t.textDim,
      fontSize: 14,
      lineHeight: 22,
      maxWidth: 440,
      marginBottom: 40,
    },
    moduleLabel: {
      borderBottomWidth: 1,
      borderBottomColor: t.borderSoft,
      paddingBottom: 8,
      marginBottom: 4,
    },
    moduleLabelText: {
      fontSize: 11,
      letterSpacing: 1.2,
      color: t.textMute,
      fontWeight: "600",
      textTransform: "uppercase",
    },
    moduleRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: t.borderSoft,
    },
    moduleIcon: {
      width: 22,
      height: 22,
      borderRadius: 5,
      backgroundColor: t.pillBg,
      borderWidth: 1,
      borderColor: t.pillBorder,
      alignItems: "center",
      justifyContent: "center",
    },
    moduleIconText: { color: t.brand2, fontSize: 11, fontWeight: "700" },
    moduleName: { color: t.text, fontWeight: "500", fontSize: 13, flex: 1 },
    moduleMeta: {
      color: t.textMute,
      fontSize: 11,
      fontFamily: "Menlo",
      letterSpacing: 0.4,
    },
    footerMeta: {
      marginTop: 32,
      fontSize: 11,
      color: t.textMute,
    },
  });

function FormPanel({ children }) {
  const { theme } = useTheme();
  return (
    <View style={[styles.formPanel, { backgroundColor: theme.bg }]}>
      <GridBackdrop />
      <View
        style={[
          styles.card,
          { backgroundColor: theme.surface, borderColor: theme.borderSoft },
        ]}
      >
        {children}
      </View>
    </View>
  );
}

function ThemedTitle({ children }) {
  const { theme } = useTheme();
  return (
    <NtText style={[styles.cardTitle, { color: theme.text }]}>{children}</NtText>
  );
}

function ThemedSubtitle({ children, style }) {
  const { theme } = useTheme();
  return (
    <NtText style={[styles.cardSub, { color: theme.textDim }, style]}>
      {children}
    </NtText>
  );
}

function AccentBar() {
  const { theme } = useTheme();
  return <View style={[styles.accentBar, { backgroundColor: theme.brand }]} />;
}

function PageWrap({ children }) {
  const { theme } = useTheme();
  const isMobile = useMobile();
  return (
    <View style={[styles.page, { backgroundColor: theme.bg }]}>
      {!isMobile && <BrandPanel />}
      <FormPanel>{children}</FormPanel>
    </View>
  );
}

class AuthController extends BaseAuthController {
  state = {
    // Pre-fill from the runtime-injected default (single-tenant container);
    // otherwise the user types their own alias on the form.
    alias: settings.defaultDomain || "",
    username: "",
    password: "",
    loading: false,
  };

  componentDidMount() {
    document.title = "Voxcraft | Sign in";
  }

  isFieldsRequired = () =>
    !!(this.state.alias && this.state.username && this.state.password);

  handleLogin = async () => {
    if (!this.isFieldsRequired()) {
      showToast("Oops...", "All fields required.", "error");
      return;
    }

    this.setState({ loading: true });

    const domain = this.state.alias.trim();

    let businessUser;
    try {
      businessUser = await getAuthService().loginBusiness(
        this.state.username,
        this.state.password,
        domain,
        // BFF device fingerprint — best-effort web heuristics. Native builds
        // can override this with richer values (UIDevice.current.name, etc.)
        // when this controller is reused on mobile.
        { deviceName: deriveDeviceName(), platform: "web" },
      );
    } catch (err) {
      showToast(
        "Oops...",
        err?.message || "Unable to process request.",
        "error",
      );
      this.setState({ loading: false });
      return;
    }

    this.setState({ username: "", password: "" });

    const permissions = await fetchPermissions(
      domain,
      businessUser.userId,
      1,
      1000000,
    );

    if (!permissions.ok) {
      showToast(
        "Oops...",
        "Unable to process request. Please try again later.",
        "error",
      );
      this.setState({ loading: false });
      return;
    }

    this.context.updateUser({
      ...this.context.user,
      ...businessUser,
      email: businessUser.email || "",
      authenticated: true,
      permissions: permissions.data?.data,
    });

    // Land on the user's configured default page (Settings › Default landing
    // page), falling back to the dashboard. The landing pref id matches the
    // route name, so it can be navigated to directly.
    const landingRoute = getPreferences().landingRoute || routes.dashboard;
    setTimeout(() => {
      navigationRef.current.navigate(landingRoute);
    }, 500);

    this.setState({ loading: false });
  };

  render() {
    const enabled = this.isFieldsRequired();
    return (
      <PageWrap>
        <AccentBar />
        <ThemedTitle>Welcome back</ThemedTitle>
        <ThemedSubtitle style={{ marginTop: 4 }}>
          Sign in to manage your PBX account.
        </ThemedSubtitle>

        {settings.defaultDomain ? null : (
          <NtTextInput
            textStyle={{ flex: 1 }}
            containerStyle={{ marginTop: 24 }}
            placeholder={"Company alias"}
            value={this.state.alias}
            autoCorrect={false}
            autoCapitalize="none"
            onChangeText={(text) => this.setState({ alias: text })}
          />
        )}

        <NtTextInput
          textStyle={{ flex: 1 }}
          containerStyle={{
            marginTop: settings.defaultDomain ? 24 : 12,
          }}
          placeholder={"Username"}
          value={this.state.username}
          autoCorrect={false}
          autoCapitalize="none"
          onChangeText={(text) => this.setState({ username: text })}
        />

        <NtTextInput
          textStyle={{ flex: 1 }}
          containerStyle={{ marginTop: 12 }}
          placeholder={"Password"}
          showEye={true}
          value={this.state.password}
          secureTextEntry={true}
          autoCorrect={false}
          autoCapitalize="none"
          onChangeText={(text) => this.setState({ password: text })}
        />

        <NtButton
          containerStyle={{ marginTop: 24 }}
          text="Sign in"
          icon="login"
          onPress={enabled ? this.handleLogin : null}
          isLoading={this.state.loading}
        />

        <ForgotPasswordLink
          alias={this.state.alias}
          username={this.state.username}
        />
      </PageWrap>
    );
  }
}

// "Forgot password?" link — prefills the recovery form with whatever the
// user already typed (alias + username/email) so they don't have to retype.
function ForgotPasswordLink({ alias, username }) {
  const { theme } = useTheme();
  return (
    <View style={{ marginTop: 16, alignItems: "center" }}>
      <Pressable
        onPress={() =>
          navigationRef.current?.navigate(routes.passwordrecovery, {
            domain: (alias || "").trim() || undefined,
            identifier: (username || "").trim() || undefined,
          })
        }
        style={({ pressed }) => [{ opacity: pressed ? 0.6 : 1 }]}
      >
        <NtText style={{ color: theme.brand2, fontSize: 13, fontWeight: "600" }}>
          Forgot your password?
        </NtText>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  page: { flex: 1, flexDirection: "row", minHeight: "100%" },
  formPanel: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 48,
    overflow: "hidden",
    position: "relative",
  },
  card: {
    width: "100%",
    maxWidth: 380,
    padding: 36,
    borderRadius: 10,
    borderWidth: 1,
    zIndex: 1,
  },
  accentBar: {
    height: 3,
    width: 36,
    borderRadius: 2,
    marginBottom: 22,
  },
  cardTitle: { fontSize: 26, fontWeight: "700", letterSpacing: -0.3 },
  cardSub: { fontSize: 13 },
});

/**
 * Best-effort device label for the BFF session list. The server caps this at
 * 128 chars and falls back to a UA-derived label if absent, so this is purely
 * a UX nicety — "Chrome on macOS" is friendlier than "Mozilla/5.0 (…)".
 */
function deriveDeviceName() {
  if (typeof navigator === "undefined" || !navigator.userAgent) return undefined;
  const ua = navigator.userAgent;
  const browser =
    /Edg\//.test(ua) ? "Edge" :
    /Chrome\//.test(ua) ? "Chrome" :
    /Firefox\//.test(ua) ? "Firefox" :
    /Safari\//.test(ua) ? "Safari" : "Browser";
  const os =
    /Mac OS X/.test(ua) ? "macOS" :
    /Windows/.test(ua) ? "Windows" :
    /Linux/.test(ua) ? "Linux" :
    /iPhone|iPad/.test(ua) ? "iOS" :
    /Android/.test(ua) ? "Android" : "Web";
  return `${browser} on ${os}`;
}

export default AuthController;
